import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BookingSearchForm from "../../components/booking/BookingSearchForm";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { MapContext } from "../../context/map.context";
import {
  BookingLocation,
  RoomType,
  useBookingSearch,
  useBookingUser,
  useBookings,
  useRegisterBookingUser,
} from "../../data/occupancy.api";
//@ts-ignore
import Mazemap from "@itpi/mazemap";
import BackButton from "../../components/BackButton";
import BookingFilters from "../../components/booking/BookingFilters";
import LocationCard from "../../components/booking/LocationCard";
import MyBookings from "../../components/booking/MyBookings";
import { AuthContext } from "../../context/auth.context";

type Props = {};

const BookingSearch = (props: Props) => {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const [deskType, setDeskType] = useState<RoomType>();
  const [searchDate, setSearchDate] = useState<string>();
  const [building, setBuilding] = useState<string>();
  const { data, isLoading, isError, fetchStatus } = useBookingSearch(
    deskType,
    searchDate,
    building
  );

  const activeData = useMemo(() => {
    return data?.filter((d) => d.active);
  }, [data]);

  const [filteredLocations, setFilteredLocations] = useState<BookingLocation[]>(
    []
  );
  const { setPins, fitBounds, reset } = useContext(MapContext);
  const navigate = useNavigate();
  const { userDetails } = useContext(AuthContext);

  useEffect(() => {
    if (data && data.length > 0 && ((searchDate && deskType) || building)) {
      Promise.all(
        //Its at this point that the key is disappearing - presumably the object
        //returned by getPoi is not the same as the original object
        data.map(async (loc) => {
          const mazemapPoi = await Mazemap.Data.getPoi(loc.poiId);
          return {
            key: loc.key,
            ...mazemapPoi,
          };
        })
      ).then((pois) => {
        const bounds = Mazemap.Util.Turf.bbox({
          type: "FeatureCollection",
          features: pois,
        });
        fitBounds(bounds, {
          padding: 50,
        });
        setPins(
          pois.map((poi) => {
            const point = Mazemap.Util.Turf.center(poi);
            return {
              lng: point.geometry.coordinates[0],
              lat: point.geometry.coordinates[1],
              zLevel: poi.properties.zLevel,
              onClick: () => {
                navigate(
                  `/book/${poi.key}` +
                    (searchDate ? `?datetime=${searchDate}` : "")
                );
              },
            };
          })
        );
      });

      return reset;
    }
  }, [
    data,
    searchDate,
    deskType,
    fitBounds,
    navigate,
    reset,
    setPins,
    building,
  ]);

  useEffect(() => {
    const dt = queryParams.get("deskType");
    const sd = queryParams.get("date");
    const bid = queryParams.get("building");
    if (
      dt &&
      sd &&
      Object.values(RoomType).includes(parseInt(dt)) &&
      moment(sd).isValid()
    ) {
      setDeskType(dt as unknown as RoomType);
      setSearchDate(sd);
      return () => {
        setDeskType(undefined);
        setSearchDate(undefined);
      };
    } else if (dt && bid && Object.values(RoomType).includes(parseInt(dt))) {
      setDeskType(dt as unknown as RoomType);
      setBuilding(bid);
      return () => {
        setDeskType(undefined);
        setBuilding(undefined);
      };
    }
  }, [queryParams]);

  const [userIsVerified, setUserIsVerified] = useState<boolean>();
  const { data: user, isError: userError } = useBookings();
  const { mutateAsync: register } = useRegisterBookingUser();

  useEffect(() => {
    // Check that an open ilancaster account is verified
    if (
      userDetails &&
      !user &&
      !userError &&
      userDetails["cognito:groups"].includes("OpenILancasterProfile")
    ) {
      setUserIsVerified(undefined);
      register();
    } else {
      setUserIsVerified(true);
    }
  }, [userDetails, register, user, userError]);

  useEffect(() => {
    if (userDetails["cognito:groups"].includes("OpenILancasterProfile")) {
      setUserIsVerified(user?.verified);
    }
  }, [user, userDetails]);

  const searchLoading = useMemo(() => {
    return isLoading && fetchStatus === "fetching";
  }, [fetchStatus, isLoading]);

  return (
    <>
      <Modal
        isOpen={userIsVerified === false}
        onClose={() => navigate("/")}
        size="xl"
        closeOnEsc={false}
        closeOnOverlayClick={false}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Awaiting Verification</ModalHeader>
          <ModalBody>
            <Text>
              To access the booking app, user verification is required. An admin
              will review and approve your account in due time. Please try again
              later once your account has been authorised.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => navigate("/")}>Go back</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {userIsVerified ? (
        <HybridBottomSheetDrawer>
          <Box p={10} position="relative">
            <BackButton />
            <Heading mb={4} size="md">
              {searchLoading
                ? "Searching campus..."
                : data
                ? "Available bookings"
                : "Start your booking"}
            </Heading>
            {searchLoading ? (
              <Center p={8}>
                <Spinner />
              </Center>
            ) : isError ? (
              <Center p={8}>
                <Alert status="error">
                  <AlertIcon />
                  Something went wrong while searching for available locations.
                </Alert>
              </Center>
            ) : activeData && ((deskType && searchDate) || building) ? (
              <>
                <Stack>
                  <BookingFilters
                    data={activeData}
                    onFiltered={setFilteredLocations}
                  />
                  {filteredLocations.length > 0 ? (
                    filteredLocations.map((location) => (
                      <LocationCard location={location} datetime={searchDate} />
                    ))
                  ) : (
                    <Alert>
                      <AlertIcon />
                      No bookings found for this search.
                    </Alert>
                  )}
                </Stack>
              </>
            ) : (
              <Tabs isFitted>
                <TabList mb="1em">
                  <Tab>Search</Tab>
                  <Tab>My Bookings</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <BookingSearchForm />
                  </TabPanel>
                  <TabPanel px={0}>
                    <MyBookings />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </Box>
        </HybridBottomSheetDrawer>
      ) : null}
    </>
  );
};

export default BookingSearch;
